//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import ClientId from '@models/ClientId';
import { IMediaOverlayMetadata } from '@models/IMediaOverlayMetadata';
import { INetworkItem } from '@models/INetworkItem';
import {
  ShareOnboardingSteps,
  ShareStatusFilterValues,
} from '@share/constants/shareConstants';

export enum PublisherModerationStatus {
  BRAND_APPROVED = 'brand-approved',
  BRAND_REJECTED = 'brand-rejected',
  NEEDS_PRODUCTS = 'needs-products',
  NEEDS_RIGHTS = 'needs-rights',
  PUBLISHED = 'published',
  UNDER_REVIEW = 'under-review',
  RIGHTS_PENDING = 'rights-pending',
}

export enum BazaarvoiceNetworkModerationStatus {
  NEEDS_PRODUCTS = 'needs-products',
  NEEDS_RIGHTS = 'needs-rights',
  DISTRIBUTED = 'distributed',
}

export enum ClientRelationshipType {
  SHARE_V1 = 'Sharev1',
  CONNECT = 'Connect',
  MODERATION_SERVICES = 'ModerationServices',
  TAGGING_SERVICES = 'TaggingServices',
}

export interface IShareMetadata {
  isRightsPending: boolean;
  publisherModerationStatus: PublisherModerationStatus;
  publisherRejectionReason: string;
  sharedTimestamp: number;
}

export interface IShareMediaItem {
  networkItem: INetworkItem;
  shareMetadata: IShareMetadata;
  mediaOverlayMetadata: IMediaOverlayMetadata;
}

export interface IShareFlags {
  isDoneFetchingMedia: boolean;
  isFetchingFilteredMedia: boolean;
  isFetchingMediaByCuid: boolean;
  isFetchingMediaAndStats: boolean;
  isFetchingNextMedia: boolean;
}

export interface IShareModerationStats {
  approvedMediaCount: number;
  actionRequiredMediaCount: number;
  publishedMediaCount: number;
  rejectedMediaCount: number;
  sharedMediaCount: number;
  underReviewMediaCount: number;
}

export interface IShareTaggedProductStats {
  productId: string;
  productImageUrl?: string;
  productTitle?: string;
  tagCount: number;
}

export interface IShareTaggedProductsStats {
  taggedProducts: IShareTaggedProductStats[];
}

export interface IShareData {
  flags: IShareFlags;
  items: IShareMediaItem[];
  moderationStats: IShareModerationStats;
  publisherClients: ISharePublisherClient[];
  taggedProductsStats: IShareTaggedProductStats[];
  totalItemCount: number;
}

export interface IShareActionBar {
  searchFilterValue: string;
  statusFilterValue: ShareStatusFilterValues;
}

export interface IShareOnboarding {
  currentOnboardingStep: ShareOnboardingSteps;
  shouldShowOnboarding: boolean;
  isStyleGuideReviewed: boolean;
  shouldShowSourceError: boolean;
  shouldShowStyleGuideAcceptanceError: boolean;
}

export interface ISharePublisherClient {
  id: ClientId;
  name: string;
  creatorBrandSkuLimit?: number;
  creatorBrandSearchFilter?: string;
  publisherStyleGuideUrl?: string;
  isModerationServices: boolean;
  isModerationServicesPublisherClient: boolean;
  isCopyCreatorLabelsEnabled: boolean;
  relationshipType: ClientRelationshipType;
  isCreatorRightsApprovalRequired: boolean;
}

export interface IShareCreatorClient {
  id: ClientId;
  name: string;
  isModerationServices: boolean;
}

export interface IShareRejectionReason {
  rejectionCode: number;
  rejectionDescription: string;
}

export interface IBazaarvoiceDestinationClient {
  id: ClientId;
  name: string;
  numItemsShared: number;
}

// VSN

export interface GetCampaignSyndicationStatsRequest {
  creatorClientId?: ClientId;
  campaignId?: string;
}

export interface CampaignDestinationProduct {
  productId?: string;
  productImageUrl?: string;
  productTitle?: string;
  destination?: string;
  numberReviews?: number;
  sharedContentCount?: number;
  publishedContentCount?: number;
}

export interface CampaignDestinationStat {
  name?: string;
  clientId?: string;
  numItemsShared?: number;
  numItemsPublished?: number;
  products?: CampaignDestinationProduct[];
}

export interface CampaignSyndicationStatsResponse {
  destinations: CampaignDestinationStat[];
}
